import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  TextField,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";

import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { fetchData } from "../../../../fetchData";

import useHrmTerminationStore from "../../../../stores/hrTermination";
import useAuthStore from "../../../../stores/auth";
import DatePickerField from "../../../../components/DatePickerField";
import DragAndDropFiles from "../../../../components/DragAndDropFiles";
import FeatureFeedback from "../../../../components/FeatureFeedback";

interface FormValues {
  type: string;
  reason: string;
  date: string;
  obs: string;
  attachment?: any;
}

const types = [
  {
    value: "1",
    label: "Trabalhado",
  },
  {
    value: "2",
    label: "Indenizado",
  },
  {
    value: "3",
    label: "Ausência/Dispensa",
  },
];

const reasons = [
  {
    value: "1",
    label: "Demissão COM Justa Causa",
  },
  {
    value: "2",
    label: "Demissão SEM Justa Causa",
  },
  {
    value: "4",
    label: "Pedido Demissão",
  },
  {
    value: "11",
    label: "Antecipação Contrato Experiência pelo Empregado",
  },
  {
    value: "10",
    label: "Antecipação Contrato Experiência pela Empresa",
  },
  {
    value: "22",
    label: "Fim do Contrato de Trabalho",
  },
  {
    value: "44",
    label: "Acordo Entre Ambas as Partes",
  },
  {
    value: "46",
    label: "Falecimento",
  },
];

const HrmEmployeeTerminationDrawer = () => {
  const company = useAuthStore((state) => state.company);
  const state = useHrmTerminationStore((state) => state.drawer);
  const employee: any = useHrmTerminationStore((state) => state.drawerData);
  const toggleDrawer = useHrmTerminationStore((state) => state.toggleDrawer);
  const [activeStep, setActiveStep] = useState(0);
  const [err, setErr] = useState(false);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(false);
  const [shouldAskFeedback, setShouldAskFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: "",
      reason: "",
      date: "",
      obs: "",
      attachment: null,
    },
  });

  const reasonValue = watch("reason");
  const isDateFieldDisabled = !reasonValue;
  const isFreeDateReason = reasonValue === "4" || reasonValue === "44";
  const minDate = isFreeDateReason ? null : dayjs();

  useEffect(() => {
    if (reasonValue) {
      setValue("date", "");
    }
  }, [reasonValue, setValue]);

  const handleNext = async () => {
    const reasonValue = watch("reason");
    const requiresAttachment =
      reasonValue === "4" || reasonValue === "11" || reasonValue === "44";

    const fieldsToValidate: (keyof FormValues)[] = ["type", "reason", "date"];
    if (requiresAttachment) {
      fieldsToValidate.push("attachment");
    }

    if (
      activeStep === 0 &&
      !(await trigger(fieldsToValidate, {
        shouldFocus: true,
      }))
    )
      return;

    if (activeStep + 1 < steps.length)
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const ErrorAlert = () => {
    return (
      <Alert
        severity="error"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
        }}
      >
        <Stack alignItems="center" direction="column">
          <Typography textAlign="center" variant="h5">
            Ops!
          </Typography>
          <Typography textAlign="center">
            Não foi possível enviar a solicitação no momento. Tente novamente
            mais tarde.
          </Typography>
          <Button
            variant="contained"
            onClick={() => toggleDrawer()}
            sx={{ mt: 2 }}
          >
            Fechar
          </Button>
        </Stack>
      </Alert>
    );
  };

  const SuccessAlert = () => {
    return (
      <>
        {!shouldAskFeedback && (
          <Alert
            severity="success"
            sx={{
              height: "100%",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "cenrer",
            }}
          >
            <Stack alignItems="center" direction="column">
              <Typography textAlign="center" variant="h5">
                Muito bem!
              </Typography>
              <Typography textAlign="center">
                Solicitação enviada com sucesso.
              </Typography>
              <Button
                variant="contained"
                onClick={() => toggleDrawer()}
                sx={{ mt: 2 }}
              >
                Fechar
              </Button>
            </Stack>
          </Alert>
        )}

        {shouldAskFeedback && !feedbackSent && (
          <FeatureFeedback
            submitting={progress}
            onSubmit={async ({ rating, feedback }) => {
              setProgress(true);
              try {
                await fetchData("/feedback/ces", {
                  method: "POST",
                  body: JSON.stringify({
                    context: "ces_fopag_termination",
                    rating,
                    comments: feedback || null,
                    clientId: company._id,
                    clientReference: company.reference,
                  }),
                });
              } catch (error) {
                console.log(error);
              }
              setProgress(false);
              setFeedbackSent(true);
            }}
            onSkip={async () => {
              setProgress(true);
              try {
                await fetchData("/feedback/skip", {
                  method: "POST",
                  body: JSON.stringify({ context: "ces_fopag_termination" }),
                });
              } catch (error) {
                console.log(error);
              }
              setProgress(false);
              toggleDrawer();
            }}
            onClose={() => toggleDrawer()}
          />
        )}

        {shouldAskFeedback && feedbackSent && (
          <Alert
            severity="success"
            sx={{
              height: "100%",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "cenrer",
            }}
          >
            <Stack alignItems="center" direction="column">
              <Typography textAlign="center" variant="h5">
                Obrigado!
              </Typography>
              <Typography textAlign="center">
                Sua avaliação foi enviada com sucesso.
              </Typography>
              <Button
                variant="contained"
                onClick={() => toggleDrawer()}
                sx={{ mt: 2 }}
              >
                Fechar
              </Button>
            </Stack>
          </Alert>
        )}
      </>
    );
  };

  const Progress = () => {
    return (
      <Alert
        severity="info"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
        }}
        icon={false}
      >
        <Stack spacing={1} alignItems="center" direction="column">
          <CircularProgress />
          <Typography textAlign="center" variant="h6">
            Enviando solicitação
          </Typography>
          <Typography textAlign="center">Aguarde...</Typography>
        </Stack>
      </Alert>
    );
  };

  const steps = [
    {
      label: "Solicitação",
      description: ``,
      outlet: (
        <Stack spacing={1}>
          <TextField
            select
            {...register("type", {
              required: "Selecione o tipo de aviso pévio",
            })}
            label="Tipo de Aviso Prévio"
            variant="filled"
            required
            value={watch("type") || ""}
          >
            {types.map((option) => (
              <MenuItem key={option.value} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {errors?.type && (
            <Alert severity="error">{errors.type.message?.toString()}</Alert>
          )}
          <TextField
            select
            required
            {...register("reason", {
              required: "Selecione o motivo de demissão",
            })}
            label="Motivo de Demissão"
            variant="filled"
            value={watch("reason") || ""}
          >
            {reasons.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {errors?.reason && (
            <Alert severity="error">{errors.reason.message?.toString()}</Alert>
          )}
          <DatePickerField
            name="date"
            register={register}
            get={getValues}
            set={setValue}
            label="Data do Aviso Prévio"
            variant="filled"
            required
            requiredtxt="Informe a data do aviso prévio."
            disabled={isDateFieldDisabled}
            minDate={minDate}
          />
          {errors?.date && (
            <Alert severity="error">{errors.date.message?.toString()}</Alert>
          )}
          <TextField
            {...register("obs")}
            label="Observações"
            variant="filled"
            multiline
            rows={4}
          />
          {(watch("reason") === "4" ||
            watch("reason") === "11" ||
            watch("reason") === "44") && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Anexo
                </Typography>
                <Tooltip
                  title={`Para esta modalidade (${
                    reasons.find((r) => r.value === watch("reason"))?.label ||
                    ""
                  }) é necessário a realização da carta a próprio punho.`}
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
              <Controller
                name="attachment"
                control={control}
                rules={{
                  validate: (value) => {
                    const reasonValue = watch("reason");
                    if (
                      (reasonValue === "4" ||
                        reasonValue === "11" ||
                        reasonValue === "44") &&
                      !value
                    ) {
                      return "Anexo é obrigatório para esta modalidade";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <DragAndDropFiles
                    baseUrl={`cloud/${company._id}?path=hr_processes/termination/${employee.i_empregados}`}
                    multiple={false}
                    allowedFiles=".pdf,.jpeg,.jpg,.png,.doc,.docx"
                    callback={(response) => {
                      setUploadedFile(response.data);
                      field.onChange(response.data);
                      trigger("attachment");
                    }}
                  />
                )}
              />
              {errors?.attachment && (
                <Alert severity="error">
                  {errors.attachment.message?.toString()}
                </Alert>
              )}
              {!uploadedFile && (
                <Typography variant="caption" color="error">
                  Anexo obrigatório para esta modalidade
                </Typography>
              )}
            </>
          )}
        </Stack>
      ),
    },
    {
      label: "Envio",
      description: "",
      outlet: <></>,
    },
  ];

  async function onSubmit(data: any) {
    setProgress(true);
    data.name = employee.nome;
    data.reference = company.reference;
    // reason: enviar código e rótulo
    const selectedReason = reasons.find((r) => r.value === data.reason);
    data.reasonCode = selectedReason ? Number(selectedReason.value) : undefined;
    data.reason = selectedReason ? selectedReason.label : data.reason;

    if (uploadedFile) {
      data.attachment = uploadedFile;
    }

    try {
      await fetchData(
        `/hrm/fopag/employee/${employee.i_empregados}/termination`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
      );
      setSuccess(true);
      try {
        const feedbackState = await fetchData("/feedback/track", {
          method: "POST",
          body: JSON.stringify({ context: "ces_fopag_termination" }),
        });
        setShouldAskFeedback(!!feedbackState?.shouldAsk);
      } catch (error) {
        console.log(error);
        setShouldAskFeedback(false);
      }
    } catch (error) {
      setErr(true);
      console.log(error);
    }

    setProgress(false);
  }

  useEffect(() => {
    if (!state) {
      reset();
      setActiveStep(0);
      setSuccess(false);
      setErr(false);
      setUploadedFile(null);
      setShouldAskFeedback(false);
      setFeedbackSent(false);
    }
  }, [state, reset]);

  return (
    <Drawer
      anchor="left"
      open={state}
      onClose={() => {
        toggleDrawer();
      }}
    >
      <Box sx={{ p: 3, pt: 2, width: 400 }} width={300} height="100%">
        <Stack height="100%" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack
              direction="row"
              spacing={1}
              sx={{ flex: 1 }}
              alignItems="center"
            >
              <Typography variant="body1">Solicitar Rescisão</Typography>
            </Stack>
            <IconButton
              onClick={() => {
                toggleDrawer();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" fontWeight={600}>
            {employee.nome}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    {step.description && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {step.description}
                      </Typography>
                    )}
                    {step.outlet}
                    {index === 0 && (
                      <Box sx={{ mb: 2 }}>
                        <div>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleNext}
                            sx={{ mt: 1, mr: 1 }}
                            type="submit"
                          >
                            Enviar
                          </Button>
                        </div>
                      </Box>
                    )}
                    {success && <SuccessAlert />}
                    {err && <ErrorAlert />}
                    {progress && <Progress />}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </form>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default HrmEmployeeTerminationDrawer;
